import { Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { styled } from "@mui/system";
import { useEffect, useState } from "react";
import { useMobileContext } from "../context/MobileContext";
import { toolTipEventBus } from "../utilities/toolTipEventBus";

interface CustomTooltipProps extends TooltipProps {
  isOnModal?: boolean;
}

const ClickableTooltip = ({ className, ...props }: CustomTooltipProps) => {
  const { isTouchDevice } = useMobileContext();

  const [open, setOpen] = useState(false);
  // CONTROLLED when the caller passes `open` (e.g. a click-to-confirm like the
  // Monitor's Nudge): the caller decides when it shows, and hover, focus, touch
  // and click stop toggling it. Every existing caller passes no `open`, so it
  // keeps the hover/tap behaviour below unchanged.
  const controlled = props.open !== undefined;

  const handleClick = () => {
    if (isTouchDevice) {
      toolTipEventBus.emit("tooltip-event-close-all"); // Close all tooltips
      setTimeout(() => setOpen(!open), 0); // Functional update form is not working, used !open.
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    if (!isTouchDevice) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (isTouchDevice) {
      const handleCloseAll = () => {
        setOpen(false);
      };
      toolTipEventBus.on("tooltip-event-close-all", handleCloseAll);
      return () => {
        toolTipEventBus.off("tooltip-event-close-all", handleCloseAll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Tooltip
      {...props}
      id={`${props.id}-customtooltip`}
      classes={{ popper: `whitespace-pre-line ${className}` }}
      open={controlled ? props.open : open}
      onClose={controlled ? props.onClose : handleClose}
      onOpen={controlled ? props.onOpen : handleOpen}
      disableHoverListener={controlled || isTouchDevice} // Disable hover if mobile
      disableFocusListener={controlled || isTouchDevice} // Optionally disable focus if mobile
      disableTouchListener={controlled || !isTouchDevice} // On mobile we rely on click, not long-press
      disableInteractive={props.disableInteractive ?? true}
      onClick={controlled ? props.onClick : handleClick}
      leaveTouchDelay={10000}
    >
      {props.children}
    </Tooltip>
  );
};

const CustomTooltip = styled(
  ({ className, isOnModal = false, ...props }: CustomTooltipProps) => (
    <ClickableTooltip
      {...props}
      id={`${props.id}-customtooltip`}
      className={className}
      PopperProps={{
        style: { zIndex: isOnModal ? 1400 : 1000 },
        ...props.PopperProps,
      }}
    />
  )
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "#344054",
    boxShadow: "0 -4px 5px -4px #FFC600, 0 4px 5px rgba(0, 0, 0, 0.3)",
    borderRadius: 6,
    position: "relative",
    maxWidth: "300px",
    padding: "12px",
    fontSize: "0.75rem",
    fontWeight: 400,
  },
  [`& .${tooltipClasses.tooltipPlacementTop}`]: {
    marginBottom: "15px !important",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#fff",
    "&::before": {
      borderWidth: "0 6px 6px 6px",
      boxShadow: "0 -4px 5px -4px #FFC600, 0 0 5px rgba(0, 0, 0, 0.3)",
      borderColor: "transparent transparent #fff transparent",
    },
  },
}));

export default CustomTooltip;
